import { Plus, Receipt } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { FormField } from '../../components/common/FormField';
import { LoadingState } from '../../components/common/LoadingState';
import { Modal } from '../../components/common/Modal';
import { createTransaction, getCourseSignups, getCourses, getStudents, getTransactions, linkTransactionToStudent } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../hooks/useTenant';
import { useToast } from '../../contexts/ToastContext';
import { formatCurrency } from '../../utils/currency';
import { formatDate, formatDateTime } from '../../utils/date';
import { sendInvoiceCommunications } from '../../services/communicationService';

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toInputDateValue(value = new Date()) {
  const date = value?.toDate ? value.toDate() : value;
  const normalized = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(normalized.getTime())) return '';
  const offset = normalized.getTimezoneOffset() * 60000;
  return new Date(normalized.getTime() - offset).toISOString().slice(0, 10);
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function formatStudentName(record) {
  return record?.fullName
    || `${record?.firstName || ''} ${record?.lastName || ''}`.trim()
    || record?.bio
    || 'Unnamed student';
}

function getInitials(name = '') {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function formatInvoiceNumber(record) {
  return record?.invoiceNumber || record?.receiptNumber || `INV-${String(record?.id || '').slice(0, 8).toUpperCase() || '00000000'}`;
}

function generateInvoiceNumber() {
  const now = new Date();
  const yearPart = now.getFullYear().toString().slice(-2);
  const monthPart = String(now.getMonth() + 1).padStart(2, '0');
  const randomPart = String(Math.floor(1000 + Math.random() * 9000));
  return `INV${yearPart}${monthPart}${randomPart}`;
}

function createDefaultValues(studentOptions = [], courses = []) {
  const defaultStudent = studentOptions[0] || null;
  const defaultCourseId = defaultStudent?.courseId || courses[0]?.id || '';
  const defaultCourse = courses.find((course) => course.id === defaultCourseId) || null;

  return {
    studentKey: defaultStudent?.key || '',
    courseId: defaultCourse?.id || '',
    amount: defaultCourse?.feeAmount ? String(defaultCourse.feeAmount) : '',
    dueDate: toInputDateValue(new Date(Date.now() + (7 * 24 * 60 * 60 * 1000))),
    note: '',
  };
}

export function InvoicesPage() {
  const { activeTenantId, activeTenant } = useTenant();
  const { user } = useAuth();
  const { notify } = useToast();
  const currency = activeTenant?.settings?.currency || 'KES';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [students, setStudents] = useState([]);
  const [signups, setSignups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [values, setValues] = useState(createDefaultValues());

  const load = useCallback(async () => {
    if (!activeTenantId) {
      setStudents([]);
      setSignups([]);
      setCourses([]);
      setTransactions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [nextStudents, nextSignups, nextCourses, loadedTransactions] = await Promise.all([
        getStudents(activeTenantId),
        getCourseSignups(activeTenantId),
        getCourses(activeTenantId),
        getTransactions(activeTenantId),
      ]);
      const signupByEmail = new Map(nextSignups
        .filter((signup) => signup.authUid && (signup.email || signup.authEmail))
        .map((signup) => [String(signup.email || signup.authEmail).trim().toLowerCase(), signup.authUid]));
      const legacyInvoices = loadedTransactions.filter((transaction) => {
        const email = String(transaction.studentEmail || '').trim().toLowerCase();
        return (transaction.type === 'invoice' || transaction.category === 'invoice')
          && !transaction.authUid
          && signupByEmail.has(email);
      });
      if (legacyInvoices.length) {
        await Promise.all(legacyInvoices.map((transaction) => linkTransactionToStudent(
          activeTenantId,
          transaction.id,
          signupByEmail.get(String(transaction.studentEmail).trim().toLowerCase()),
        )));
      }
      setStudents(nextStudents);
      setSignups(nextSignups);
      setCourses(nextCourses.filter((course) => course.status !== 'deleted'));
      setTransactions(loadedTransactions.map((transaction) => {
        const authUid = signupByEmail.get(String(transaction.studentEmail || '').trim().toLowerCase());
        return authUid && !transaction.authUid ? { ...transaction, authUid } : transaction;
      }));
    } catch (error) {
      console.error('Unable to load invoices', error);
      setStudents([]);
      setSignups([]);
      setCourses([]);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [activeTenantId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  const courseLookup = useMemo(
    () => new Map(courses.map((course) => [course.id, course])),
    [courses],
  );

  const studentOptions = useMemo(() => {
    const nextOptions = [];
    const seen = new Set();

    const pushRecord = (record) => {
      const name = formatStudentName(record);
      const key = `${record.source}:${record.id}`;
      const dedupeKey = record.email
        ? `email:${normalizeText(record.email)}`
        : record.authUid
          ? `auth:${record.authUid}`
          : key;

      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);

      nextOptions.push({
        key,
        source: record.source,
        id: record.id,
        label: name,
        courseId: record.courseId || '',
        courseName: record.courseName || '',
        email: record.email || '',
        phone: record.phone || '',
        authUid: record.authUid || '',
      });
    };

    signups.forEach((item) => pushRecord({ ...item, source: 'application' }));
    students.forEach((item) => pushRecord({ ...item, source: 'student' }));

    return nextOptions.sort((a, b) => a.label.localeCompare(b.label));
  }, [signups, students]);

  const invoiceRows = useMemo(() => {
    return transactions
      .filter((item) => String(item.category || item.type || '') === 'invoice' || String(item.type || '') === 'invoice')
      .sort((a, b) => {
        const dateA = toDate(a.createdAt)?.getTime() || 0;
        const dateB = toDate(b.createdAt)?.getTime() || 0;
        return dateB - dateA;
      });
  }, [transactions]);

  const totalInvoiceAmount = useMemo(
    () => invoiceRows.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    [invoiceRows],
  );

  const currentPageAmount = useMemo(() => {
    const pageSize = 8;
    const startIndex = (currentPage - 1) * pageSize;
    return invoiceRows.slice(startIndex, startIndex + pageSize).reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }, [currentPage, invoiceRows]);

  const uniqueStudentsCount = useMemo(
    () => new Set(invoiceRows.map((item) => item.studentId || item.studentEmail || item.studentName).filter(Boolean)).size,
    [invoiceRows],
  );

  const latestInvoiceRecord = invoiceRows[0] || null;
  const pageSize = 8;
  const totalPages = Math.max(1, Math.ceil(invoiceRows.length / pageSize));
  const currentRecords = invoiceRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const selectedStudent = useMemo(
    () => studentOptions.find((item) => item.key === values.studentKey) || null,
    [studentOptions, values.studentKey],
  );

  const selectedCourse = useMemo(
    () => courseLookup.get(values.courseId) || null,
    [courseLookup, values.courseId],
  );

  function openComposer() {
    setValues(createDefaultValues(studentOptions, courses));
    setOpen(true);
  }

  function closeComposer() {
    setOpen(false);
    setSaving(false);
  }

  function handleFieldChange(event) {
    const { name, value } = event.target;

    if (name === 'studentKey') {
      const nextStudent = studentOptions.find((item) => item.key === value) || null;
      const nextCourse = nextStudent?.courseId ? courseLookup.get(nextStudent.courseId) || null : null;
      setValues((current) => ({
        ...current,
        studentKey: value,
        courseId: nextStudent?.courseId || current.courseId,
        amount: nextCourse?.feeAmount ? String(nextCourse.feeAmount) : current.amount,
      }));
      return;
    }

    if (name === 'courseId') {
      const nextCourse = courseLookup.get(value) || null;
      setValues((current) => ({
        ...current,
        courseId: value,
        amount: nextCourse?.feeAmount ? String(nextCourse.feeAmount) : current.amount,
      }));
      return;
    }

    setValues((current) => ({ ...current, [name]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!activeTenantId || saving) return;

    if (!selectedStudent) {
      notify('Please pick a student before sending the invoice.', 'error');
      return;
    }

    if (!selectedCourse) {
      notify('Please pick a course for this invoice.', 'error');
      return;
    }

    const amount = Number(values.amount || 0);
    if (!amount || amount <= 0) {
      notify('Please enter a valid invoice amount.', 'error');
      return;
    }

    if (!values.dueDate) {
      notify('Please pick a due date for this invoice.', 'error');
      return;
    }

    setSaving(true);
    try {
      const invoiceNumber = generateInvoiceNumber();
      const invoice = {
        invoiceNumber,
        studentName: selectedStudent.label,
        studentEmail: selectedStudent.email || '',
        studentPhone: selectedStudent.phone || '',
        courseName: selectedCourse.name,
        amount,
        dueDate: values.dueDate,
        note: values.note || '',
      };
      await createTransaction(activeTenantId, user?.uid, {
        ...invoice,
        type: 'invoice',
        category: 'invoice',
        source: selectedStudent.source || 'manual',
        studentId: selectedStudent.id || null,
        authUid: selectedStudent.authUid || null,
        courseId: selectedCourse.id,
        paymentDate: values.dueDate,
        paymentMethod: 'invoice',
        reference: invoice.note,
        status: 'sent',
      });

      const communicationResults = await sendInvoiceCommunications({
        user,
        invoice,
        academyName: activeTenant?.name,
        currency,
      });
      communicationResults.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`${index === 0 ? 'Invoice email' : 'Invoice SMS'} could not be sent`, result.reason);
        }
      });

      notify('The invoice has been saved successfully.');
      closeComposer();
      await load();
    } catch (error) {
      console.error('Unable to send invoice', error);
      notify('Please try again in a moment.', 'error');
      setSaving(false);
    }
  }

  if (loading) {
    return <LoadingState label="Loading invoices..." />;
  }

  return (
    <div className="invoicePage">
      <div className="invoiceHero">
        <div className="invoiceHeroCopy">
          <div className="invoiceHeroTitleRow">
            <h5>Sent Invoices</h5>
          </div>
          <p className="invoiceHeaderMeta">
            <span>{invoiceRows.length} records</span>
            <span className="invoiceInlineDot" />
            <span>{uniqueStudentsCount} students</span>
            <span className="invoiceInlineDot" />
            <span>Newest first</span>
          </p>
        </div>
        <div className="invoiceHeroActions">
          <Button icon={Plus} onClick={openComposer}>Send an Invoice</Button>
        </div>
      </div>

      <div className="invoiceStatsGrid">
        <div className="invoiceStatCard invoiceStatPrimary">
          <span className="invoiceStatLabel">Total Invoiced</span>
          <h3>{formatCurrency(totalInvoiceAmount, currency)}</h3>
          <p>All invoices currently loaded.</p>
        </div>
        <div className="invoiceStatCard">
          <span className="invoiceStatLabel">Students Covered</span>
          <h3>{uniqueStudentsCount}</h3>
          <p>Unique students with invoice records.</p>
        </div>
        <div className="invoiceStatCard">
          <span className="invoiceStatLabel">This Page Total</span>
          <h3>{formatCurrency(currentPageAmount, currency)}</h3>
          <p>{currentRecords.length} invoices in this view.</p>
        </div>
        <div className="invoiceStatCard">
          <span className="invoiceStatLabel">Latest Sent Invoice</span>
          <h3>{latestInvoiceRecord ? formatCurrency(latestInvoiceRecord.amount, currency) : 'No invoices'}</h3>
          <p>{latestInvoiceRecord ? `${latestInvoiceRecord.studentName || 'Unknown student'} • ${formatDateTime(latestInvoiceRecord.createdAt)}` : 'New invoices will appear here.'}</p>
        </div>
      </div>

      <div className="invoiceToolbar">
        <div className="invoiceToolbarMeta">
          <span className="invoiceToolbarText">Page {currentPage} of {totalPages || 1}</span>
        </div>
        <div className="invoiceToolbarMeta">
          <span className="invoiceToolbarText">Showing {currentRecords.length} of {invoiceRows.length} records</span>
        </div>
      </div>

      {invoiceRows.length === 0 ? (
        <EmptyState
          title="No invoices yet"
          message="Once an invoice is sent, it will appear here with the newest one at the top."
          actionLabel="Send the First Invoice"
          onAction={openComposer}
          icon={Receipt}
        />
      ) : (
        <>
          <div className="invoiceTableCard">
            <div className="invoiceTableHeader">
              <div>
                <h6>Recorded Invoices</h6>
                <p>Recent invoices sent to students.</p>
              </div>
            </div>
            <div className="invoiceTableWrap">
              <table className="invoiceRecordsTable">
                <thead>
                  <tr>
                    <th>Invoice Date</th>
                    <th>Student Name</th>
                    <th>Course</th>
                    <th>Amount</th>
                    <th>Due Date</th>
                    <th>View Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRecords.map((record) => {
                    const studentName = record.studentName || 'Unknown student';
                    const studentMeta = [record.studentEmail, record.studentPhone].filter(Boolean).join(' • ');

                    return (
                      <tr key={record.id}>
                        <td>
                          <div className="invoiceDateCell">
                            <strong>{formatDateTime(record.createdAt)}</strong>
                            <span>Invoice #{formatInvoiceNumber(record)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="invoiceStudentCell">
                            <div className="invoiceAvatar" aria-hidden="true">
                              {getInitials(studentName)}
                            </div>
                            <div>
                              <p className="invoiceStudentName">{studentName}</p>
                              <span className="invoiceStudentMeta">{studentMeta || 'Student invoice record'}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="invoiceCoursePill">{record.courseName || '—'}</span>
                        </td>
                        <td>
                          <span className="invoiceAmountText">{formatCurrency(record.amount, currency)}</span>
                        </td>
                        <td>
                          <span className="invoiceDueDateText">{record.dueDate ? formatDate(toDate(record.dueDate)) : '—'}</span>
                        </td>
                        <td>
                          <Button
                            to={`/dashboard/finance/invoices/${record.id}`}
                            variant="secondary"
                            icon={Receipt}
                            className="invoiceViewButton"
                          >
                            View Invoice
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="invoicePaginationWrap">
            <button
              className="invoicePageButton"
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage <= 1}
            >
              Previous
            </button>
            <div className="invoicePageButtons">
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                <button
                  key={pageNumber}
                  className={`invoicePageButton ${pageNumber === currentPage ? 'active' : ''}`.trim()}
                  type="button"
                  onClick={() => setCurrentPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
            </div>
            <button
              className="invoicePageButton"
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={currentPage >= totalPages}
            >
              Next
            </button>
          </div>
        </>
      )}

      <Modal
        className="modal-invoice"
        open={open}
        onClose={closeComposer}
        title="Send Invoice"
        description="Create a simple student invoice with the due date and amount."
      >
        <form className="modal-form invoiceComposerSimple" onSubmit={handleSubmit}>
          <div className="invoiceComposerSummaryRow">
            <div>
              <span>Student</span>
              <strong>{selectedStudent?.label || 'Select a student'}</strong>
            </div>
            <div>
              <span>Course</span>
              <strong>{selectedCourse?.name || 'No course selected'}</strong>
            </div>
            <div>
              <span>Due date</span>
              <strong>{values.dueDate ? formatDate(toDate(values.dueDate)) : '—'}</strong>
            </div>
          </div>

          <div className="addInvoiceFormGrid invoiceComposerFields">
            <div className="addInvoiceFormFullWidth">
              <FormField label="Student" id="invoice-student">
                <select id="invoice-student" name="studentKey" className="form-control" value={values.studentKey} onChange={handleFieldChange}>
                  <option value="">Select a student</option>
                  {studentOptions.map((student) => (
                    <option key={student.key} value={student.key}>{student.label}</option>
                  ))}
                </select>
              </FormField>
            </div>

            <FormField label="Course" id="invoice-course">
              <select id="invoice-course" name="courseId" className="form-control" value={values.courseId} onChange={handleFieldChange}>
                <option value="">Select a course</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>{course.name}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Amount" id="invoice-amount">
              <input
                id="invoice-amount"
                name="amount"
                type="number"
                min="0"
                step="0.01"
                className="form-control"
                placeholder="Enter invoice amount"
                value={values.amount}
                onChange={handleFieldChange}
              />
            </FormField>

            <FormField label="Due Date" id="invoice-due-date">
              <input
                id="invoice-due-date"
                name="dueDate"
                type="date"
                className="form-control"
                value={values.dueDate}
                onChange={handleFieldChange}
              />
            </FormField>

            <div className="addInvoiceFormFullWidth">
              <FormField label="Note" id="invoice-note">
                <textarea
                  id="invoice-note"
                  name="note"
                  className="form-control"
                  rows={3}
                  placeholder="Optional note for the student"
                  value={values.note}
                  onChange={handleFieldChange}
                />
              </FormField>
            </div>
          </div>

          <div className="invoiceComposerMiniNote">
            <span>Preview</span>
            <strong>{formatCurrency(Number(values.amount || 0), currency)} due {values.dueDate ? `by ${formatDate(toDate(values.dueDate))}` : 'when due date is set'}</strong>
            <p>The invoice will be saved in finance and can be reopened from Sent Invoices.</p>
          </div>

          <div className="invoiceComposerFooter">
            <span className="invoiceComposerFooterHint">The invoice is saved as a tenant record once you send it.</span>
            <div className="modal-footer invoiceComposerActions">
              <Button type="button" variant="secondary" onClick={closeComposer}>Cancel</Button>
              <Button type="submit" loading={saving}>Send Invoice</Button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
